import { createAsyncThunk } from '@reduxjs/toolkit';
import { PlayerPosition } from 'modules/teams/domain/enums/player-position';
import type { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import { addPlayerToTeam, removePlayerFromTeam } from 'modules/teams/store/teams';
import { USER_DEFAULT_AVATAR } from 'shared/constants/links';
import { buildTournamentApplicationUserDto } from 'shared/utils/build-tournament-application-user-dto';
import { ChangeParticipantsRoleDto } from 'modules/teams/domain/dtos/change-participants-role';
import { KickFromTeamInputDto } from 'modules/teams/domain/dtos/kick-from-team-input.dto';
import { UpsertTournamentApplicationUserDto } from 'modules/teams/domain/dtos/upsert-tournament-application-user.dto';
import { UpsertTournamentPlayerApplicationDto } from 'modules/teams/domain/dtos/upsert-tournament-player-action.dto';
import { TournamentUser } from 'modules/teams/domain/interfaces/tournament-user';
import playersRepository from 'modules/teams/domain/repositories/players.repository';
import playersResource from 'modules/teams/domain/resources/players-resource';

const PREFIX = 'teams/players';

export const getTeamPlayers = createAsyncThunk<TeamPlayer[], number>(`${PREFIX}/getTeamPlayers`, async (payload) => {
  try {
    return await playersRepository.getTeamMembers(payload);
  } catch (err) {
    console.error(`${PREFIX}/getTeamPlayers error:`, err);
    throw err;
  }
});

export const upsertTournamentPlayerApplication = createAsyncThunk<TournamentUser, UpsertTournamentPlayerApplicationDto>(
  `${PREFIX}/upsertTournamentPlayerApplication`,
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const { tournamentId, userId, photo, teamId } = payload;
      let player: UpsertTournamentApplicationUserDto = buildTournamentApplicationUserDto(payload);

      let playerId: number | null = null;

      try {
        let result = await playersRepository.upsertTournamentApplicationForUser(player, tournamentId);
        playerId = result.id;
      } catch (err) {
        if (err === 'User is already in this team') {
          console.warn(`${PREFIX}/upsertTournamentPlayerApplication warning:`, err);
        } else {
          console.error(`${PREFIX}/upsertTournamentPlayerApplication error:`, err);
        }

        if (userId) {
          playerId = userId;
        }
      }

      if (!playerId) {
        throw new Error('Player ID not found');
      }

      if (photo && photo[0]) {
        await playersResource.uploadPhoto(photo[0], playerId, tournamentId);
      }

      if (!userId) {
        await playersRepository.addUserToTeam({
          userId: playerId,
          teamId,
        });
      }

      const user = await playersRepository.getUser(playerId);

      dispatch(
        addPlayerToTeam({
          teamId,
          player: {
            ...user,
            userId: user.id,
            isReal: false,
            firstName: user.name,
            middleName: user.middleName,
            lastName: user.surname,
            avatar: user.photo?.externalUrl ?? USER_DEFAULT_AVATAR,
            position: PlayerPosition.DEFENDER,
            number: 99,
          },
        }),
      );

      return user;
    } catch (error) {
      console.error(`${PREFIX}/upsertTournamentPlayerApplication error:`, error);
      let message = error instanceof Error ? error.message : String(error);
      return rejectWithValue(message);
    }
  },
);

export const deleteTournamentPlayerApplication = createAsyncThunk<KickFromTeamInputDto, KickFromTeamInputDto>(
  `${PREFIX}/deleteTournamentPlayerApplication`,
  async (payload, { dispatch }) => {
    try {
      const { userId, teamId } = payload;

      dispatch(removePlayerFromTeam(payload));
      await playersRepository.kickUserFromTeam({ teamId, userId });

      return payload;
    } catch (err) {
      console.error(`${PREFIX}/deleteTournamentPlayerApplication error:`, err);
      throw err;
    }
  },
);

export const changePlayerRole = createAsyncThunk<string, ChangeParticipantsRoleDto>(`${PREFIX}/changePlayerRole`, async (payload) => {
  try {
    return await playersResource.changeRole(payload.teamId, payload.userId, payload.role, payload.number, payload.position);
  } catch (err) {
    console.error(`${PREFIX}/changePlayerRole error:`, err);
    throw err;
  }
});
